<script lang="ts">
  import {
    mdiAccountQuestionOutline,
    mdiCalendarEdit,
    mdiCheckAll,
    mdiClose,
    mdiCloseCircleOutline,
    mdiKeyVariant,
  } from "@mdi/js"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type { PublicAppointmentPortalStateResponse } from "../../../../../../../../api/public/appointment-portal"
  import {
    formatAppointmentSentenceDate,
    formatAppointmentSentenceTimeRange,
    formatAppointmentHeading,
    invalidPortalStatePresentation,
    stateLabel,
  } from "../../_shared/_logic/appointment-portal"
  import { joinGermanList } from "../../../../../../../../_shared/_logic/order-type-labels"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"

  let {
    portalState,
    busy = false,
    onconfirm,
    onchooseSlot,
    oncancel,
    onwrongRecipient,
    onresetCode,
  }: {
    portalState: PublicAppointmentPortalStateResponse
    busy?: boolean
    onconfirm: () => void
    onchooseSlot: () => void
    oncancel: () => void
    onwrongRecipient: () => void
    onresetCode: () => void
  } = $props()

  const canSearch = $derived(portalState.actions.includes("search_slots"))
  const canConfirm = $derived(portalState.actions.includes("confirm"))
  const canCancel = $derived(portalState.actions.includes("cancel"))
  const canWrongRecipient = $derived(portalState.actions.includes("wrong_recipient"))
  const appointmentDateLabel = $derived(formatAppointmentSentenceDate(portalState.appointment))
  const appointmentTimeRangeLabel = $derived(
    formatAppointmentSentenceTimeRange(portalState.appointment),
  )
  const appointmentHeading = $derived(formatAppointmentHeading(portalState.appointment))
  const propertyLabel = $derived(portalState.property?.label ?? "Adresse aus Auftrag")
  const workOrderTypeLabels = $derived(
    (portalState.work_order?.order_type_public_labels ?? [])
      .map((label) => label.trim())
      .filter(Boolean),
  )
  const workOrderTypeLabel = $derived(
    portalState.work_order?.order_type_public_label?.trim() || joinGermanList(workOrderTypeLabels),
  )
  const isMultiOrderType = $derived(workOrderTypeLabels.length > 1)
  const workOrderInstruction = $derived(
    portalState.work_order?.order_type_description?.trim() ?? "",
  )
  const workOrderDurationLabel = $derived(
    portalState.work_order?.estimated_duration_min
      ? `${portalState.work_order.estimated_duration_min} Minuten`
      : "",
  )
  const appointmentEmployeeLabel = $derived(
    portalState.appointment?.employee_name?.trim() || "Noch nicht festgelegt",
  )
  const invalidPresentation = $derived(invalidPortalStatePresentation(portalState.invalid_reason))
  const panelTitle = $derived(
    portalState.state === "invalid" ? invalidPresentation.title : stateLabel(portalState.state),
  )
  const slotActionLabel = $derived(
    portalState.state === "no_appointment"
      ? "Termin wählen"
      : portalState.state === "confirmed"
        ? "Termin verschieben"
        : "Termin passt nicht",
  )
  const slotActionIcon = $derived(
    portalState.state === "no_appointment" ? mdiCalendarEdit : mdiClose,
  )
  const slotActionType = $derived(
    portalState.state === "confirmed"
      ? "tertiaryButton"
      : canConfirm
        ? "secondaryButton"
        : "primaryButton",
  )
</script>

<SectionTitle
  title={appointmentHeading}
  small={true}
  brightBackground={true}
  subtitle={panelTitle}
  style="margin-bottom: 1.2rem;"
></SectionTitle>
{#if portalState.state === "invalid"}
  <p class="message">{invalidPresentation.description}</p>
  <CTA
    type="primaryButton"
    icon={mdiKeyVariant}
    content="Code erneut eingeben"
    darkMode={false}
    onClick={onresetCode}
  />
{:else if portalState.state === "locked"}
  <p class="message">
    Dieser Auftrag kann online nicht mehr geändert werden. Bitte wenden Sie sich direkt an den
    Betrieb.
  </p>
  {#if portalState.organization?.contact_email}
    <a class="contact-link" href={`mailto:${portalState.organization.contact_email}`}>
      {portalState.organization.contact_email}
    </a>
  {/if}
{:else}
  <div class="status-content">
    <div class="details">
      <ActionBox label="Terminvereinbarung">
        {#snippet children()}
          <p class="detail-copy">
            {#if portalState.appointment}
              Der Schornsteinfeger kommt am <strong>{appointmentDateLabel}</strong> zwischen
              <strong>{appointmentTimeRangeLabel}</strong>. Die Arbeiten werden von
              <strong>{appointmentEmployeeLabel}</strong> durchgeführt.
            {:else}
              Es ist noch kein Termin vereinbart.
            {/if}
          </p>
        {/snippet}
      </ActionBox>
      <ActionBox
        label="Auftrag Nr. {portalState.work_order?.order_number ??
          portalState.work_order?.order_type_public_label ??
          'Ihr Auftrag'}"
      >
        {#snippet children()}
          <p class="detail-copy">
            {#if workOrderTypeLabel && workOrderDurationLabel}
              {#if isMultiOrderType}
                Für die Arbeiten der Typen <strong>{workOrderTypeLabel}</strong> sind
                <strong>{workOrderDurationLabel}</strong> eingeplant.
              {:else}
                Für die Arbeiten vom Typ <strong>{workOrderTypeLabel}</strong> sind
                <strong>{workOrderDurationLabel}</strong> eingeplant.
              {/if}
            {:else if workOrderDurationLabel}
              Für diese Arbeiten sind <strong>{workOrderDurationLabel}</strong> eingeplant.
            {:else if workOrderTypeLabel}
              {#if isMultiOrderType}
                Geplant sind Arbeiten der Typen <strong>{workOrderTypeLabel}</strong>.
              {:else}
                Für die Arbeiten vom Typ <strong>{workOrderTypeLabel}</strong> ist noch keine Dauer hinterlegt.
              {/if}
            {:else}
              Für diese Arbeiten ist noch keine Dauer hinterlegt.
            {/if}
            {#if workOrderInstruction}
              &nbsp;{workOrderInstruction}
            {/if}
          </p>
        {/snippet}
      </ActionBox>
      <ActionBox label="Terminadresse">
        {#snippet children()}
          <p class="detail-copy">
            Die Adresse, an der die Arbeiten stattfinden sollen, ist <strong>{propertyLabel}</strong
            >.
          </p>
        {/snippet}
      </ActionBox>
    </div>

    <div class="actions">
      {#if canConfirm}
        <CTA
          type="primaryButton"
          icon={mdiCheckAll}
          content={busy ? "Wird bestätigt" : "Termin bestätigen"}
          darkMode={false}
          disabled={busy}
          onClick={onconfirm}
        />
      {/if}
      {#if canSearch}
        <CTA
          type={slotActionType}
          icon={slotActionIcon}
          content={slotActionLabel}
          darkMode={false}
          disabled={busy}
          onClick={onchooseSlot}
        />
      {/if}
      {#if canCancel}
        <CTA
          type="tertiaryButton"
          icon={mdiCloseCircleOutline}
          content="Termin stornieren"
          darkMode={false}
          disabled={busy}
          style="border-color:#b42318;color:#b42318;"
          onClick={oncancel}
        />
      {/if}
      {#if canWrongRecipient}
        <CTA
          type="tertiaryButton"
          icon={mdiAccountQuestionOutline}
          content="Ich bin nicht zuständig"
          darkMode={false}
          disabled={busy}
          onClick={onwrongRecipient}
        />
      {/if}
    </div>
  </div>
{/if}

<style>
  *,
  p {
    color: var(--text-invers-100);
  }

  .details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem;
    margin-bottom: 1.2rem;
  }

  .details :global(.box) {
    background: #fff;
    color: var(--portal-ink);
    border-color: var(--portal-border);
  }

  strong {
    display: block;
    font-size: 0.98rem;
    overflow-wrap: anywhere;
    color: var(--portal-ink);
  }

  .details p,
  .message {
    margin: 0;
  }

  .detail-copy {
    line-height: 1.35;
    color: var(--portal-ink);
  }

  .detail-copy + .detail-copy {
    margin-top: 0.75rem;
  }

  .detail-copy strong {
    display: inline;
  }

  .message {
    max-width: 760px;
  }

  .actions {
    display: flex;
    gap: 0.7rem;
    flex-wrap: wrap;
    justify-content: flex-start;
    flex-direction: row-reverse;
  }

  .contact-link {
    color: var(--primary-200);
    font-weight: 800;
  }

  @media (max-width: 820px) {
    .status-content {
      display: flex;
      flex-direction: column;
    }

    .details {
      grid-template-columns: 1fr;
      margin-bottom: 0;
    }

    .actions {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.7rem;
      order: -1;
      margin-bottom: 1.2rem;
    }
  }
</style>
